import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './FantasyPreview.css';

interface TeamRankings {
  teamId: number;
  teamName: string;
  ownerName: string;
  projectedRanking: number;
  projectedFloor: number;
  projectedCeiling: number;
  qbRank: number;
  rbRank: number;
  wrRank: number;
  teRank: number;
  longTermValueRank: number;
  featuredPlayerImage?: string;
  commentary: string;
  teamLogo?: string;
}

const MOCK_TEAM_DATA: TeamRankings[] = [
  {
    teamId: 1,
    teamName: "Championship Contenders",
    ownerName: "Owner 1",
    projectedRanking: 1,
    projectedFloor: 3,
    projectedCeiling: 1,
    qbRank: 2,
    rbRank: 1,
    wrRank: 3,
    teRank: 4,
    longTermValueRank: 2,
    commentary: "This team is loaded with talent across all positions. Strong depth at RB and elite QB play should carry them through the season. The addition of a top-tier WR in the draft gives them the firepower needed for a championship run."
  },
  {
    teamId: 2,
    teamName: "Dynasty Builders",
    ownerName: "Owner 2",
    projectedRanking: 2,
    projectedFloor: 4,
    projectedCeiling: 1,
    qbRank: 1,
    rbRank: 5,
    wrRank: 1,
    teRank: 8,
    longTermValueRank: 1,
    commentary: "Built for the future with young superstars at every key position. While they may struggle with depth this year, their core is championship-caliber for years to come."
  },
  {
    teamId: 3,
    teamName: "Veteran Squad",
    ownerName: "Owner 3",
    projectedRanking: 3,
    projectedFloor: 1,
    projectedCeiling: 6,
    qbRank: 3,
    rbRank: 2,
    wrRank: 4,
    teRank: 1,
    longTermValueRank: 7,
    commentary: "Experience and consistency define this roster. They may not have the highest ceiling, but their veteran presence and reliable floor make them a playoff lock."
  },
  {
    teamId: 4,
    teamName: "Sleeper Picks",
    ownerName: "Owner 4",
    projectedRanking: 4,
    projectedFloor: 2,
    projectedCeiling: 8,
    qbRank: 6,
    rbRank: 3,
    wrRank: 5,
    teRank: 3,
    longTermValueRank: 4,
    commentary: "This team is full of high-upside players who could breakout. If their young talent develops as expected, they could surprise everyone and make a deep playoff run."
  },
  {
    teamId: 5,
    teamName: "Solid Foundation",
    ownerName: "Owner 5",
    projectedRanking: 5,
    projectedFloor: 5,
    projectedCeiling: 3,
    qbRank: 4,
    rbRank: 6,
    wrRank: 2,
    teRank: 7,
    longTermValueRank: 5,
    commentary: "A well-balanced team with no glaring weaknesses. Their elite WR corps could carry them to the playoffs if everything falls into place."
  },
  {
    teamId: 6,
    teamName: "High Risk High Reward",
    ownerName: "Owner 6",
    projectedRanking: 6,
    projectedFloor: 9,
    projectedCeiling: 2,
    qbRank: 5,
    rbRank: 4,
    wrRank: 8,
    teRank: 2,
    longTermValueRank: 8,
    commentary: "Boom or bust perfectly describes this roster. They have the talent to compete with anyone on any given week, but consistency issues could derail their season."
  },
  {
    teamId: 7,
    teamName: "Rebuilding Year",
    ownerName: "Owner 7",
    projectedRanking: 7,
    projectedFloor: 6,
    projectedCeiling: 9,
    qbRank: 8,
    rbRank: 7,
    wrRank: 6,
    teRank: 5,
    longTermValueRank: 3,
    commentary: "This team is clearly in rebuild mode, but they've made smart moves for the future. Don't expect much this year, but they'll be dangerous in 2-3 seasons."
  },
  {
    teamId: 8,
    teamName: "Question Marks",
    ownerName: "Owner 8",
    projectedRanking: 8,
    projectedFloor: 7,
    projectedCeiling: 5,
    qbRank: 7,
    rbRank: 9,
    wrRank: 7,
    teRank: 6,
    longTermValueRank: 9,
    commentary: "Too many uncertainties surround this roster. Health concerns and age-related decline could make this a challenging season for what was once a powerhouse team."
  },
  {
    teamId: 9,
    teamName: "Dark Horse",
    ownerName: "Owner 9",
    projectedRanking: 9,
    projectedFloor: 8,
    projectedCeiling: 4,
    qbRank: 9,
    rbRank: 8,
    wrRank: 9,
    teRank: 9,
    longTermValueRank: 6,
    commentary: "Don't sleep on this team. While they may not look impressive on paper, they have a knack for finding value and could surprise people if their sleepers hit."
  },
  {
    teamId: 10,
    teamName: "Bottom Feeders",
    ownerName: "Owner 10",
    projectedRanking: 10,
    projectedFloor: 10,
    projectedCeiling: 7,
    qbRank: 10,
    rbRank: 10,
    wrRank: 10,
    teRank: 10,
    longTermValueRank: 10,
    commentary: "A tough season ahead for this squad. Limited talent and poor draft capital make it difficult to see a path to success. Focus should be on development and future planning."
  }
];

export const FantasyPreview: React.FC = () => {
  const [teamsData, setTeamsData] = useState<TeamRankings[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading data - replace with actual API call
    setTimeout(() => {
      setTeamsData(MOCK_TEAM_DATA);
      setLoading(false);
    }, 1000);
  }, []);

  if (loading) {
    return (
      <div className="fantasy-preview-loading">
        <h2>Loading Fantasy Preview...</h2>
      </div>
    );
  }

  return (
    <div className="fantasy-preview-container">
      <header className="magazine-header">
        <h1 className="magazine-title">Fantasy Football Preview 2025</h1>
        <p className="magazine-subtitle">Your Complete Guide to League Domination</p>
        <div className="magazine-date">Season Preview | July 2025</div>
      </header>

      <nav className="preview-navigation">
        <Link to="/fantasy-preview" className="nav-link active">
          Overall Rankings
        </Link>
        {teamsData.map(team => (
          <Link 
            key={team.teamId} 
            to={`/fantasy-preview/team/${team.teamId}`} 
            className="nav-link"
          >
            {team.teamName}
          </Link>
        ))}
      </nav>

      <main className="overall-rankings">
        <section className="rankings-header">
          <h2>2025 Pre-Season Power Rankings</h2>
          <p>Our comprehensive analysis of all 10 teams heading into the new season</p>
        </section>

        <div className="rankings-grid">
          {teamsData
            .sort((a, b) => a.projectedRanking - b.projectedRanking)
            .map((team, index) => (
              <div key={team.teamId} className="team-ranking-card">
                <div className="ranking-number">#{team.projectedRanking}</div>
                <div className="team-info">
                  <h3 className="team-name">{team.teamName}</h3>
                  <p className="owner-name">Managed by {team.ownerName}</p>
                  <div className="quick-stats">
                    <div className="stat">
                      <span className="stat-label">Floor:</span>
                      <span className="stat-value">#{team.projectedFloor}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Ceiling:</span>
                      <span className="stat-value">#{team.projectedCeiling}</span>
                    </div>
                  </div>
                  <div className="position-ranks">
                    <span className="pos-rank qb">QB: #{team.qbRank}</span>
                    <span className="pos-rank rb">RB: #{team.rbRank}</span>
                    <span className="pos-rank wr">WR: #{team.wrRank}</span>
                    <span className="pos-rank te">TE: #{team.teRank}</span>
                  </div>
                  <div className="long-term-value">
                    <span>Long-term Value: #{team.longTermValueRank}</span>
                  </div>
                </div>
                <Link 
                  to={`/fantasy-preview/team/${team.teamId}`} 
                  className="view-team-btn"
                >
                  View Full Analysis →
                </Link>
              </div>
            ))}
        </div>

        <section className="methodology">
          <h3>Ranking Methodology</h3>
          <p>
            Our rankings consider multiple factors including roster talent, depth, 
            injury concerns, schedule difficulty, and long-term potential. Each team 
            is evaluated across all position groups with special attention to their 
            floor and ceiling outcomes for the season.
          </p>
        </section>
      </main>
    </div>
  );
};

export default FantasyPreview;
